import fs from "node:fs/promises";
import path from "node:path";

function normalize(p: string): string {
  return p.split(path.sep).join("/");
}

/**
 * For v3.0.0 "Elite Governance", we want an ULTRA-CLEAN root.
 * Only "opencode" is symlinked to the root (required for OpenCode discovery).
 * All other assets stay inside .ai-workflow/.
 */
const LINKABLE_ROOTS = new Set([
  "opencode",
  ".agents"
]);

const LINKABLE_FILES = new Set<string>([]);

export interface BuildSymlinkEntriesOptions {
  templateFiles: Record<string, string>;
  installRoot?: string;
}

export interface SymlinkEntry {
  linkPath: string;
  targetPath: string;
}

export function buildSymlinkEntries({ templateFiles, installRoot = ".ai-workflow" }: BuildSymlinkEntriesOptions): SymlinkEntry[] {
  const entries = new Map<string, string>();

  for (const relativePath of Object.keys(templateFiles)) {
    if (relativePath === "opencode.jsonc") continue;

    const normalized = normalize(relativePath);

    if (LINKABLE_FILES.has(normalized)) {
      entries.set(normalized, `${installRoot}/${normalized}`);
      continue;
    }

    // Match composite paths in LINKABLE_ROOTS (e.g., "docs/policies")
    for (const rootPath of LINKABLE_ROOTS) {
      if (normalized === rootPath || normalized.startsWith(`${rootPath}/`)) {
        entries.set(rootPath, `${installRoot}/${rootPath}`);
        break;
      }
    }
  }

  return Array.from(entries.entries()).map(([linkPath, targetPath]) => ({ linkPath, targetPath }));
}

export async function isSymlinkTo(absoluteLinkPath: string, absoluteTargetPath: string): Promise<boolean> {
  try {
    const stat = await fs.lstat(absoluteLinkPath);
    if (!stat.isSymbolicLink()) return false;
    const currentTarget = await fs.readlink(absoluteLinkPath);
    const resolved = path.resolve(path.dirname(absoluteLinkPath), currentTarget);
    return resolved === absoluteTargetPath;
  } catch {
    return false;
  }
}

/**
 * Internal symlink to satisfy opencode.jsonc paths without polluting the root.
 * Creates .ai-workflow/opencode/skills -> ../.agents/skills
 */
export async function setupInternalSymlinks(cwd: string, installRoot: string = ".ai-workflow"): Promise<void> {
  const skillsLink = path.join(cwd, installRoot, "opencode/skills");
  const skillsTarget = path.join(cwd, installRoot, ".agents/skills");

  if (!(await exists(skillsTarget))) return;

  try {
    const stat = await fs.lstat(skillsLink).catch(() => null);
    if (stat) {
      if (stat.isSymbolicLink()) {
        const current = await fs.readlink(skillsLink);
        if (current === "../.agents/skills") return;
        await fs.unlink(skillsLink);
      } else {
        return; // File exists, skip
      }
    }

    const type = process.platform === "win32" ? "junction" : "dir";
    await fs.symlink("../.agents/skills", skillsLink, type);
  } catch {
    // Silent fail for internal symlink
  }
}

async function exists(p: string): Promise<boolean> {
  try {
    await fs.access(p);
    return true;
  } catch {
    return false;
  }
}
